import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Button,
  Typography,
  Box,
  LinearProgress,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Chip,
  Divider,
  Paper,
  Drawer,
  Grid
} from '@mui/material';
import {
  Close as CloseIcon,
  Euro as EuroIcon,
  Percent as PercentIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon,
  Calculate as CalculateIcon,
  FileDownload as FileDownloadIcon,
  FileCopy as FileCopyIcon,
  Visibility as VisibilityIcon,
  Check as CheckIcon 
} from '@mui/icons-material';
import { createBulkFromPotentialActivities } from '../../../../helper/BillingHelper/BillingHelper';
import { API_BASE_URL } from '../../../../helper/UrlHelper';
import SplitBillingConfiguration from './SplitBillingConfiguration';

const AddBillingActivities = ({ 
  open, 
  onClose, 
  selectedActivities = [],
  onSuccess 
}) => {
  // Estados principales
  const [activeStep, setActiveStep] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  // Estados de precios y splits
  const [activityPricing, setActivityPricing] = useState(new Map());
  const [activitySplits, setActivitySplits] = useState(new Map());
  const [editingPricing, setEditingPricing] = useState(null);

  // Estados de datos de pagadores
  const [availablePatients, setAvailablePatients] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [loadingPayers, setLoadingPayers] = useState(false);
  const [patientsData, setPatientsData] = useState([]);
  const [companiesData, setCompaniesData] = useState([]);

  // Resultado de creación de actividades
  const [createdActivities, setCreatedActivities] = useState(null);

  // 🆕 Estados para Sales Orders
  const [createdSalesOrders, setCreatedSalesOrders] = useState(null);
  const [isCreatingSalesOrders, setIsCreatingSalesOrders] = useState(false);
  const [salesOrderError, setSalesOrderError] = useState(null);

  // 🆕 Estados para exportación a Odoo
  const [isExportingToOdoo, setIsExportingToOdoo] = useState(false);
  const [exportResults, setExportResults] = useState(null);

  const wizardSteps = [
  'Configurar Precios y Splits', 
  'Sales Orders y Exportación a Odoo'
];

  // ============================================
  // 📥 FETCH: CARGAR PACIENTES
  // ============================================
  const fetchAvailablePatients = async () => {
    try {
      setLoadingPayers(true);
      console.log('🔍 Cargando pacientes disponibles...');
      
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/account/getallappusers?token=${token}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al cargar pacientes');
      }

      const result = await response.json();
      
      const allPatients = result.data?.filter(user => 
        user.userrole === 'Patient' || 
        user.role?.slug === 'PATIENT' || 
        user.role?.name === 'Patient'
      ) || [];
      
      const formattedPatients = allPatients.map(patient => ({
        _id: patient._id,
        customid: patient.customid,
        name: patient.name,
        surname: patient.surname,
        fullName: `${patient.name} ${patient.surname || ''}`.trim(),
        email: patient.email || null,
        phone: patient.phone || null,
        address: patient.address || null,
        city: patient.city || null,
        state: patient.state || null,
        zipcode: patient.zipcode || null,
        type: 'Account',
        display_name: `${patient.name} ${patient.surname || ''}`.trim(),
        type_description: 'Paciente'
      }));
      
      console.log('✅ Pacientes cargados:', formattedPatients.length);
      setAvailablePatients(formattedPatients);
      setPatientsData(formattedPatients);
      return formattedPatients;
      
    } catch (error) {
      console.error('❌ Error cargando pacientes:', error);
      setAvailablePatients([]);
      return [];
    } finally {
      setLoadingPayers(false);
    }
  };

  // ============================================
  // 📥 FETCH: CARGAR COMPAÑÍAS/ASEGURADORAS
  // ============================================
  const fetchAvailableCompanies = async () => {
    try {
      setLoadingPayers(true);
      console.log('🔍 Cargando compañías disponibles...');
      
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/company/getcompanyselectlist?token=${token}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al cargar compañías');
      }

      const result = await response.json();
      
      let companies = [];
      if (result.status && Array.isArray(result.data)) {
        companies = result.data;
      } else if (Array.isArray(result)) {
        companies = result;
      }
      
      const formattedCompanies = companies.map(company => ({
        _id: company._id,
        id: company._id,
        name: company.name,
        cif: company.cif || null,
        email: company.email || null,
        phone: company.phone || null,
        website: company.website || null,
        address: company.location || null,
        city: company.city || null,
        state: company.state || null,
        country: company.country || null,
        zipcode: company.zipcode || null,
        companyType: company.companytype || 'Aseguradora',
        logo: company.logopath || null,
        type: 'Company',
        display_name: company.name,
        type_description: 'Compañía'
      }));
      // ✅ FILTRAR DUPLICADOS POR _id
const uniqueCompanies = formattedCompanies.filter((company, index, self) =>
  index === self.findIndex((c) => c._id === company._id)
);

// ✅ LOG PARA DETECTAR DUPLICADOS
const duplicateNames = formattedCompanies.filter((company, index, self) =>
  self.findIndex(c => c.name === company.name && c._id !== company._id) !== -1
);

if (duplicateNames.length > 0) {
  console.warn('⚠️ Compañías con nombres duplicados:', 
    duplicateNames.map(c => ({ name: c.name, _id: c._id }))
  );
}
      console.log('✅ Compañías únicas cargadas:', uniqueCompanies.length);
setAvailableCompanies(uniqueCompanies);
setCompaniesData(uniqueCompanies);
return uniqueCompanies;
      
    } catch (error) {
      console.error('❌ Error cargando compañías:', error);
      setAvailableCompanies([]);
      return [];
    } finally {
      setLoadingPayers(false);
    }
  };

// ============================================
// 🎬 INICIALIZACIÓN OPTIMIZADA
// ============================================
useEffect(() => {
  if (open && selectedActivities.length > 0) {
    // ✅ INICIALIZAR PRECIOS INMEDIATAMENTE (síncrono)
    initializePricingSync();
    
    // Luego cargar datos de pagadores de forma asíncrona
    initializePayersAsync();
  }
}, [open, selectedActivities]);

// ✅ NUEVA FUNCIÓN: Inicializar precios de forma síncrona (instantánea)
const initializePricingSync = () => {
  console.log('⚡ Inicializando precios de forma síncrona...');
  
  // Inicializar precios INMEDIATAMENTE
  const defaultPricingMap = new Map();
  selectedActivities.forEach(activity => {
    const key = `${activity.source_id}_${activity.activity_type}`;
    defaultPricingMap.set(key, {
      unit_price: activity.product_price || 75,
      qty: 1,
      discount: 0,
      tax_profile: 'EXENTO',
      tax_rate: 0,
      notes: ''
    });
  });
  setActivityPricing(defaultPricingMap);
  console.log('✅ Precios inicializados instantáneamente');
};

// ✅ NUEVA FUNCIÓN: Cargar pagadores de forma asíncrona (SIN setIsCreating)
const initializePayersAsync = async () => {
  // ❌ ELIMINADO: setIsCreating(true);
  
  try {
    // Cargar datos de pagadores
    const [patients, companies] = await Promise.all([
      fetchAvailablePatients(),
      fetchAvailableCompanies()
    ]);

    console.log('✅ Datos de pagadores cargados:', {
      patients: patients.length,
      companies: companies.length
    });

    // Inicializar splits por defecto del paciente al 100%
    const defaultSplitsMap = new Map();
    
    for (const activity of selectedActivities) {
      const key = `${activity.source_id}_${activity.activity_type}`;
      
      const patient = patients.find(p => p._id === activity.patient_id);
      
      if (!patient) {
        console.warn(`⚠️ Paciente ${activity.patient_id} no encontrado`);
      }
      
      defaultSplitsMap.set(key, {
        splits: [{
          payer_id: patient?._id || activity.patient_id,
          payer_type: 'Account',
          percent: 100,
          max_amount: null,
          payer_snapshot: {
            name: patient ? `${patient.name} ${patient.surname}` : activity.patient_name,
            display_name: patient ? `${patient.name} ${patient.surname}` : activity.patient_name,
            type_description: 'Paciente',
            vat: patient?._id || activity.patient_id,
            contact: {
              phone: patient?.phone || '',
              email: patient?.email || ''
            },
            address: {
              street: patient?.address || '',
              city: patient?.city || '',
              state: patient?.state || '',
              zipcode: patient?.zipcode || ''
            }
          }
        }],
        isValid: true
      });
    }
    
    setActivitySplits(defaultSplitsMap);
    console.log('✅ Splits inicializados correctamente');
    
  } catch (error) {
    console.error('❌ Error inicializando pagadores:', error);
    setError('Error al cargar datos de pagadores');
  }
  // ❌ ELIMINADO: finally { setIsCreating(false); }
};

  // ============================================
  // 🔧 FUNCIONES DE PRECIOS
  // ============================================
  const getActivityPricing = (activityKey) => {
    return activityPricing.get(activityKey) || {
      unit_price: 75,
      qty: 1,
      discount: 0,
      tax_profile: 'EXENTO',
      tax_rate: 0,
      notes: ''
    };
  };

  const handleActivityPricingChange = (activityKey, field, value) => {
    const currentPricing = getActivityPricing(activityKey);
    const newPricing = { ...currentPricing, [field]: value };
    
    if (field === 'tax_profile') {
      newPricing.tax_rate = value === 'IVA21' ? 21 : 0;
    }
    
    const newMap = new Map(activityPricing);
    newMap.set(activityKey, newPricing);
    setActivityPricing(newMap);
  };

  const calculateActivityTotal = (pricing) => {
    const subtotal = (pricing.unit_price * pricing.qty) - pricing.discount;
    const taxAmount = subtotal * (pricing.tax_rate / 100);
    return subtotal + taxAmount;
  };

  const applyBulkPricing = () => {
    const defaultPrice = prompt('Ingrese el precio unitario a aplicar a todas las actividades:', '75');
    if (defaultPrice === null) return;
    
    const price = parseFloat(defaultPrice);
    if (isNaN(price) || price < 0) {
      alert('Precio inválido');
      return;
    }

    const newMap = new Map(activityPricing);
    selectedActivities.forEach(activity => {
      const key = `${activity.source_id}_${activity.activity_type}`;
      const current = getActivityPricing(key);
      newMap.set(key, { ...current, unit_price: price });
    });
    setActivityPricing(newMap);
  };

  // ============================================
  // 🔧 FUNCIONES DE SPLITS
  // ============================================
  const handleSplitChange = (activityKey, splits, isValid) => {
  console.log('🔄 handleSplitChange llamado:', { 
    activityKey, 
    splits_count: splits?.length, 
    isValid,
    splits_data: splits
  });
  
  // ✅ CALCULAR isValid SI NO SE PASA
  let calculatedIsValid = isValid;
  if (typeof isValid !== 'boolean' && splits && splits.length > 0) {
    const totalPercent = splits.reduce((sum, s) => sum + (s.percent || 0), 0);
    calculatedIsValid = Math.abs(totalPercent - 100) < 0.01;
    console.log('🧮 isValid calculado automáticamente:', calculatedIsValid, 'Total:', totalPercent);
  }
  
  setActivitySplits(prevSplits => {
    const newMap = new Map(prevSplits);
    newMap.set(activityKey, {
      splits: splits || [],
      isValid: calculatedIsValid === true
    });
    
    console.log('💾 Activity splits actualizado para', activityKey, ':', {
      splits_count: splits?.length,
      isValid: calculatedIsValid === true,
      splits_preview: splits
    });
    
    return newMap;
  });
};

  const areAllSplitsValid = () => {
  console.log('🔍 Validando splits de todas las actividades...');
  console.log('📊 Estado actual de activitySplits:', Array.from(activitySplits.entries()));
  
  let allValid = true;
  const invalidActivities = [];
  
  for (let activity of selectedActivities) {
    const key = `${activity.source_id}_${activity.activity_type}`;
    const splitConfig = activitySplits.get(key);
    
    console.log(`🔍 Validando actividad ${key}:`, splitConfig);
    
    // Validación 1: Debe existir configuración
    if (!splitConfig) {
      console.warn(`❌ Split inexistente para ${key}`);
      invalidActivities.push({
        activity: activity.description,
        issue: 'No hay configuración de splits'
      });
      allValid = false;
      continue;
    }

    // Validación 2: Debe tener al menos un split
    if (!splitConfig.splits || splitConfig.splits.length === 0) {
      console.warn(`❌ Split vacío para ${key}`);
      invalidActivities.push({
        activity: activity.description,
        issue: 'No hay splits configurados'
      });
      allValid = false;
      continue;
    }

    // Validación 3: Verificar porcentajes
    const totalPercent = splitConfig.splits.reduce((sum, s) => sum + (s.percent || 0), 0);
    console.log(`📊 Total porcentajes para ${key}: ${totalPercent}%`);
    
    if (Math.abs(totalPercent - 100) > 0.01) {
      console.warn(`❌ Splits para ${key} suman ${totalPercent}% en lugar de 100%`);
      invalidActivities.push({
        activity: activity.description,
        issue: `Splits suman ${totalPercent.toFixed(2)}% en lugar de 100%`
      });
      allValid = false;
      continue;
    }

    // Validación 4: Verificar datos básicos de cada split
    let hasInvalidSplit = false;
    for (let i = 0; i < splitConfig.splits.length; i++) {
      const split = splitConfig.splits[i];
      
      if (!split.payer_id) {
        console.warn(`❌ Split ${i} sin payer_id en ${key}`);
        invalidActivities.push({
          activity: activity.description,
          issue: `Split ${i + 1}: sin payer_id`
        });
        hasInvalidSplit = true;
      }
      
      if (!['Account', 'Company'].includes(split.payer_type)) {
        console.warn(`❌ Split ${i} con payer_type inválido en ${key}:`, split.payer_type);
        invalidActivities.push({
          activity: activity.description,
          issue: `Split ${i + 1}: payer_type inválido`
        });
        hasInvalidSplit = true;
      }
    }
    
    if (hasInvalidSplit) {
      allValid = false;
    }
  }
  
  if (allValid) {
    console.log('✅ Todos los splits válidos y correctos');
  } else {
    console.error('❌ Problemas encontrados:', invalidActivities);
  }
  
  return allValid;
};

  // ============================================
  // 🔧 CALCULAR TOTALES
  // ============================================
  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;
    let total = 0;

    selectedActivities.forEach(activity => {
      const key = `${activity.source_id}_${activity.activity_type}`;
      const pricing = getActivityPricing(key);
      const activitySubtotal = (pricing.unit_price * pricing.qty) - pricing.discount;
      const activityTax = activitySubtotal * (pricing.tax_rate / 100);
      
      subtotal += activitySubtotal;
      totalTax += activityTax;
      total += activitySubtotal + activityTax;
    });
    return { subtotal, totalTax, total };
  };

  // ============================================
  // 🚀 CREAR ACTIVIDADES FACTURABLES
  // ============================================
  const handleCreateBillableActivities = async () => {
  console.log('🎯 handleCreateBillableActivities INICIADO');
  
  setIsCreating(true);
  setError(null);
  
  try {
    console.log('🚀 Validando datos antes de crear actividades...');
    console.log('📊 selectedActivities:', selectedActivities);
    console.log('📊 activitySplits:', Array.from(activitySplits.entries()));
    console.log('📊 activityPricing:', Array.from(activityPricing.entries()));
    
    // Validar campos requeridos
    const missingFields = selectedActivities.filter(a => !a.source_id || !a.activity_type);
    if (missingFields.length > 0) {
      console.error('❌ Actividades con campos faltantes:', missingFields);
      throw new Error(
        `${missingFields.length} actividad(es) no tienen source_id o activity_type. ` +
        `Verifica que las actividades vengan correctamente del componente padre.`
      );
    }
    
    // Validar splits válidos
    const splitsValid = areAllSplitsValid();
    console.log('🔍 Resultado de areAllSplitsValid():', splitsValid);
    
    if (!splitsValid) {
      throw new Error('No todos los splits están configurados correctamente o sumen 100%');
    }
    
    // Validar pricing
    selectedActivities.forEach(activity => {
      const key = `${activity.source_id}_${activity.activity_type}`;
      const pricing = getActivityPricing(key);
      console.log(`💰 Pricing para ${key}:`, pricing);
      
      if (!pricing || pricing.unit_price <= 0) {
        throw new Error(`Actividad ${activity.description} no tiene precio válido`);
      }
    });
    
    console.log('✅ Validaciones pasadas, construyendo payload...');
    
    // Construir payload
    const activitiesData = selectedActivities.map(activity => {
      const key = `${activity.source_id}_${activity.activity_type}`;
      const pricing = getActivityPricing(key);
      const splitConfig = activitySplits.get(key);
      
      console.log(`🔧 Procesando actividad: ${activity.description}`, {
        key,
        pricing,
        splitConfig
      });
      
      if (!splitConfig || !splitConfig.splits || splitConfig.splits.length === 0) {
        throw new Error(`No hay splits configurados para: ${activity.description}`);
      }

      const activityTotal = calculateActivityTotal(pricing);
      const splitTotalPercent = splitConfig.splits.reduce((sum, s) => sum + (s.percent || 0), 0);
      
      if (Math.abs(splitTotalPercent - 100) > 0.01) {
        throw new Error(
          `Los splits de "${activity.description}" suman ${splitTotalPercent.toFixed(2)}% en lugar de 100%`
        );
      }

      // Enriquecer splits
      const enrichedSplits = splitConfig.splits.map((split, splitIndex) => {
        console.log(`Procesando split ${splitIndex + 1} para ${activity.description}:`, {
          payer_id: split.payer_id,
          payer_type: split.payer_type,
          percent: split.percent,
          has_snapshot: !!split.payer_snapshot
        });
        
        if (!['Account', 'Company'].includes(split.payer_type)) {
          throw new Error(
            `Split inválido en "${activity.description}": ` +
            `payer_type debe ser Account o Company. Recibido: ${split.payer_type}`
          );
        }

        if (!split.payer_id) {
          throw new Error(`Split inválido en "${activity.description}": payer_id es requerido`);
        }

        if (typeof split.percent !== 'number' || split.percent <= 0) {
          throw new Error(
            `Split inválido en "${activity.description}": ` +
            `percent debe ser un número > 0. Recibido: ${split.percent}`
          );
        }

        let payerSnapshot = split.payer_snapshot || {};

        if (!payerSnapshot.name) {
          if (split.payer_type === 'Account' && split.payer_id === activity.patient_id) {
            payerSnapshot = {
              name: activity.patient_name,
              display_name: activity.patient_name,
              type_description: 'Paciente',
              vat: activity.patient_id,
              contact: {
                phone: activity.patient_phone || '',
                email: activity.patient_email || ''
              },
              address: activity.patient_address || {}
            };
            console.log(`✅ Patient payer snapshot creado: ${payerSnapshot.name}`);
          } else if (split.payer_type === 'Company') {
            const company = availableCompanies.find(c => c._id === split.payer_id);
            if (company) {
              payerSnapshot = {
                name: company.name,
                display_name: company.display_name || company.name,
                type_description: 'Compañía',
                cif: company.cif || '',
                vat: company.cif || '',
                contact: {
                  phone: company.phone || '',
                  email: company.email || ''
                },
                address: {
                  street: company.address || '',
                  city: company.city || '',
                  state: company.state || '',
                  zipcode: company.zipcode || ''
                }
              };
              console.log(`✅ Company payer snapshot creado: ${payerSnapshot.name}`);
            } else {
              throw new Error(
                `Compañía ${split.payer_id} no encontrada. ` +
                `Compañías disponibles: ${availableCompanies.map(c => c._id).join(', ')}`
              );
            }
          } else {
            throw new Error(
              `No se puede crear payer_snapshot: ` +
              `payer_type=${split.payer_type}, payer_id=${split.payer_id}`
            );
          }
        }
        
        return {
          payer_id: split.payer_id,
          payer_type: split.payer_type,
          percent: split.percent,
          max_amount: split.max_amount || null,
          payer_snapshot: payerSnapshot,
          vat: split.payer_type === 'Account' ? split.payer_id : payerSnapshot.cif || null
        };
      });

      return {
        activityType: activity.source_ref_type,
        activityId: activity.source_id,
        patientId: activity.patient_id,
        description: activity.description,
        unit_price: pricing.unit_price,
        quantity: pricing.qty,
        discount_amount: pricing.discount || 0,
        tax_percentage: pricing.tax_rate || 0,
        notes: pricing.notes || '',
        payment_splits: enrichedSplits
      };
    });

    console.log('📦 Payload construido completamente:', {
      activities_count: activitiesData.length,
      payload: activitiesData
    });
    
    // Llamar API
    console.log('🌐 Llamando a createBulkFromPotentialActivities...');
    const response = await createBulkFromPotentialActivities({
      activities: activitiesData
    });

    console.log('📥 Respuesta recibida:', response);

    // ✅ CORRECCIÓN: Validar correctamente success o status
    if (response && (response.success === true || response.status === true)) {
      setCreatedActivities(response.data);
      console.log('✅ Actividades creadas exitosamente:', response.data);
      
      // ✅ CREAR SALES ORDERS AUTOMÁTICAMENTE
      if (response.data && response.data.created && response.data.created.length > 0) {
        console.log('📄 Creando Sales Orders automáticamente...');
        await handleCreateSalesOrdersAutomatic(response.data.created);
      } else {
        console.warn('⚠️ No hay actividades creadas para generar Sales Orders');
      }
      
      // ✅ AVANZAR AL PASO 1
      console.log('➡️ Avanzando al paso 1');
      setActiveStep(1);
      
    } else {
      // ✅ SOLO lanzar error si realmente hubo un error
      const errorMessage = response?.message || response?.error || 'Error en la respuesta del servidor';
      console.error('❌ Error del servidor:', errorMessage);
      throw new Error(errorMessage);
    }
    
  } catch (error) {
    console.error('❌ Error creando actividades:', error);
    console.error('❌ Stack trace:', error.stack);
    setError(error.message || 'Error desconocido');
    alert(`❌ ERROR: ${error.message}`);
  } finally {
    setIsCreating(false);
    console.log('🏁 handleCreateBillableActivities FINALIZADO');
  }
};

  // ============================================
  // 🆕 CREAR SALES ORDERS
  // ============================================

// ✅ FUNCIÓN CORREGIDA: Crear Sales Orders automáticamente
const handleCreateSalesOrdersAutomatic = async (createdActivitiesArray) => {
  setIsCreatingSalesOrders(true);
  setSalesOrderError(null);
  
  try {
    console.log('📄 Creando Sales Orders automáticamente...');
    
    if (!createdActivitiesArray || createdActivitiesArray.length === 0) {
      throw new Error('No hay actividades creadas para generar Sales Orders');
    }

    const activityIds = createdActivitiesArray.map(act => act._id);
    
    const payload = {
      activity_ids: activityIds,
      grouping_mode: 'PAYER_PATIENT',
      invoice_date: null,
      payer_override: null
    };

    const token = localStorage.getItem('token');
    const response = await fetch(
      `${API_BASE_URL}/billing/sales-orders/create-from-activities?token=${token}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      }
    );

    const result = await response.json();
    
    if (!response.ok || !result.success) {
      throw new Error(result.message || 'Error al crear Sales Orders');
    }

    setCreatedSalesOrders(result.data);
    console.log('✅ Sales Orders creados:', result.data);

    // 🆕 GENERAR JSON PREVIEW AUTOMÁTICAMENTE
    if (result.data.sales_orders && result.data.sales_orders.length > 0) {
      console.log('📋 Generando JSON preview automáticamente...');
      
      const salesOrderIds = result.data.sales_orders.map(so => so.sales_order_id);
      
      const previewResponse = await fetch(
        `${API_BASE_URL}/billing/sales-orders/generate-odoo-json-preview?token=${token}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            sales_order_ids: salesOrderIds
          })
        }
      );

      const previewResult = await previewResponse.json();
      
      if (previewResult.success) {
        console.log('✅ JSON preview generado automáticamente');
        
        // Actualizar createdSalesOrders con los JSON
        const updatedSalesOrders = { ...result.data };
        updatedSalesOrders.sales_orders = updatedSalesOrders.sales_orders.map(so => {
          const preview = previewResult.data.find(p => p.sales_order_id === so.sales_order_id);
          return {
            ...so,
            odoo_json_preview: preview?.json_preview || null
          };
        });
        
        setCreatedSalesOrders(updatedSalesOrders);
      }
    }
    
  } catch (error) {
    console.error('❌ Error creando Sales Orders:', error);
    setSalesOrderError(error.message);
  } finally {
    setIsCreatingSalesOrders(false);
  }
};

// ✅ FUNCIÓN CORREGIDA: Exportar UN SOLO Sales Order a Odoo
const handleExportSingleToOdoo = async (salesOrderId, orderNumber) => {
  const updatedSalesOrders = { ...createdSalesOrders };
  const soIndex = updatedSalesOrders.sales_orders.findIndex(
    so => so.sales_order_id === salesOrderId
  );
  
  if (soIndex === -1) {
    alert('Sales Order no encontrado');
    return;
  }

  updatedSalesOrders.sales_orders[soIndex].exporting = true;
  setCreatedSalesOrders(updatedSalesOrders);
  
  try {
    console.log(`🚀 Generando JSON y exportando Sales Order ${orderNumber} a Odoo...`);
    
    const token = localStorage.getItem('token');
    
    // ✅ PASO 1: Generar JSON sin enviar a Odoo
    console.log('📋 Paso 1: Generando JSON...');
    const previewResponse = await fetch(
      `${API_BASE_URL}/billing/export-to-odoo?token=${token}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sales_order_ids: [salesOrderId],
          preview_only: true  // ✅ Modo preview
        })
      }
    );

    const previewResult = await previewResponse.json();
    
    console.log('════════════════════════════════════════════════════════');
    console.log('📋 JSON GENERADO (PREVIEW):');
    console.log(JSON.stringify(previewResult, null, 2));
    console.log('════════════════════════════════════════════════════════');
    
    // ✅ Guardar el JSON en el estado ANTES de exportar
    const newSalesOrdersPreview = { ...createdSalesOrders };
    newSalesOrdersPreview.sales_orders[soIndex].odoo_json_preview = previewResult.data;
    newSalesOrdersPreview.sales_orders[soIndex].exporting = false;
    setCreatedSalesOrders(newSalesOrdersPreview);
    
    // ✅ PASO 2: Confirmar antes de enviar
    const confirmSend = window.confirm(
      `✅ JSON generado correctamente.\n\n` +
      `Sales Order: ${orderNumber}\n` +
      `¿Deseas ENVIAR este JSON a Odoo ahora?\n\n` +
      `(Puedes ver el JSON completo en la consola del navegador)`
    );
    
    if (!confirmSend) {
      console.log('⏸️ Usuario canceló el envío a Odoo');
      return;
    }
    
    // ✅ PASO 3: Enviar a Odoo
    console.log('🚀 Paso 2: Enviando a Odoo...');
    newSalesOrdersPreview.sales_orders[soIndex].exporting = true;
    setCreatedSalesOrders(newSalesOrdersPreview);
    
    const response = await fetch(
      `${API_BASE_URL}/billing/export-to-odoo?token=${token}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sales_order_ids: [salesOrderId]
        })
      }
    );

    const result = await response.json();
    
    console.log('════════════════════════════════════════════════════════');
    console.log('📋 RESPUESTA COMPLETA DE ODOO:');
    console.log(JSON.stringify(result, null, 2));
    console.log('════════════════════════════════════════════════════════');
    
    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || result.message || 'Error al exportar a Odoo');
    }

    // Actualizar con el resultado COMPLETO
    const newSalesOrders = { ...createdSalesOrders };
    newSalesOrders.sales_orders[soIndex].exporting = false;
    newSalesOrders.sales_orders[soIndex].exported = true;
    newSalesOrders.sales_orders[soIndex].odoo_result = result.data.exported[0] || result.data.errors[0];
    
    setCreatedSalesOrders(newSalesOrders);
    
    if (result.data.exported && result.data.exported.length > 0) {
      console.log(`✅ Sales Order ${orderNumber} exportado exitosamente`);
      alert(`✅ Sales Order ${orderNumber} exportado exitosamente a Odoo`);
    } else {
      console.warn(`⚠️ JSON generado pero no se pudo enviar a Odoo`);
      alert(`⚠️ JSON generado correctamente. Ver consola para detalles.\n\nError: ${result.data.errors[0]?.error}`);
    }
    
  } catch (error) {
    console.error(`❌ Error exportando Sales Order ${orderNumber}:`, error);
    
    const newSalesOrders = { ...createdSalesOrders };
    newSalesOrders.sales_orders[soIndex].exporting = false;
    newSalesOrders.sales_orders[soIndex].export_error = error.message;
    setCreatedSalesOrders(newSalesOrders);
    
    alert(`❌ Error: ${error.message}`);
  }
};

  // ============================================
  // 🆕 EXPORTAR A ODOO
  // ============================================
  const handleExportToOdoo = async () => {
    setIsExportingToOdoo(true);
    setExportResults(null);
    
    try {
      console.log('🚀 Exportando a Odoo...');
      
      if (!createdSalesOrders || !createdSalesOrders.sales_orders || createdSalesOrders.sales_orders.length === 0) {
        throw new Error('No hay Sales Orders para exportar');
      }

      const salesOrderIds = createdSalesOrders.sales_orders.map(so => so.sales_order_id);
      
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/billing/export-to-odoo?token=${token}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            sales_order_ids: salesOrderIds
          })
        }
      );

      const result = await response.json();
      
      if (!response.ok || result.error) {
        throw new Error(result.error?.message || 'Error al exportar a Odoo');
      }

      setExportResults(result.data);
      setActiveStep(3); // Avanzar a resultado final
      console.log('✅ Exportación a Odoo completada');
      
    } catch (error) {
      console.error('❌ Error exportando a Odoo:', error);
      setExportResults({
        exported: [],
        errors: [{
          sales_order_id: 'ALL',
          error: error.message
        }],
        summary: {
          exported: 0,
          errors: 1
        }
      });
    } finally {
      setIsExportingToOdoo(false);
    }
  };

  // ============================================
  // 🆕 FUNCIONES DE MANEJO DE JSON
  // ============================================
  const handleViewJson = (odooJson) => {
    const jsonStr = JSON.stringify(odooJson, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadJson = (odooJson, orderNumber) => {
    const jsonStr = JSON.stringify(odooJson, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sales_order_${orderNumber || Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleCopyJson = async (odooJson) => {
    try {
      const jsonStr = JSON.stringify(odooJson, null, 2);
      await navigator.clipboard.writeText(jsonStr);
      alert('JSON copiado al portapapeles');
    } catch (error) {
      console.error('Error copiando JSON:', error);
      alert('Error al copiar JSON');
      }
};
// ============================================
// 🎨 RENDERIZAR PASOS
// ============================================
const renderStep = () => {
const totals = calculateTotals();
switch (activeStep) {
  case 0: // Configurar precios y splits
    return (
      <div className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6>
            <EuroIcon className="me-2" style={{ verticalAlign: 'middle' }} />
            Configuración de Precios y Pagadores
          </h6>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={applyBulkPricing}
          >
            <CalculateIcon style={{ fontSize: '16px', marginRight: '4px', verticalAlign: 'middle' }} />
            Precio Masivo
          </button>
        </div>

        <div className="alert alert-info mb-3">
          Configura el precio y los pagadores para cada actividad. Los copagos deben sumar 100%.
        </div>

        {/* Lista de actividades */}
        <div className="row">
          {selectedActivities.map((activity) => {
            const activityKey = `${activity.source_id}_${activity.activity_type}`;
            const pricing = getActivityPricing(activityKey);
            const total = calculateActivityTotal(pricing);
            const splitConfig = activitySplits.get(activityKey);
            const isEditing = editingPricing === activityKey;

            return (
              <div key={activityKey} className="col-12 mb-4">
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{activity.description}</strong>
                        <div>
                          <small className="text-muted">
                            <span className="badge bg-secondary me-1">{activity.activity_type}</span>
                            <span className="badge bg-light text-dark me-1 border">Paciente: {activity.patient_name}</span>
                          </small>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="h5 mb-0 text-success">
                          {new Intl.NumberFormat('es-ES', {
                            style: 'currency',
                            currency: 'EUR'
                          }).format(total)}
                        </div>
                        <small className={`badge ${splitConfig?.isValid ? 'bg-success' : 'bg-warning'}`}>
                          {splitConfig?.isValid ? 'Split válido' : 'Configurar split'}
                        </small>
                        <button
                          className={`btn btn-sm ms-2 ${isEditing ? 'btn-success' : 'btn-outline-secondary'}`}
                          onClick={() => setEditingPricing(isEditing ? null : activityKey)}
                        >
                          {isEditing ? <SaveIcon style={{ fontSize: '16px' }} /> : <EditIcon style={{ fontSize: '16px' }} />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    {/* Pricing Configuration */}
                    {isEditing && (
                      <div className="bg-light p-3 rounded mb-3">
                        <h6 className="text-primary mb-3">Configurar Precio</h6>
                        <div className="row g-3">
                          <div className="col-md-3">
                            <label className="form-label">Precio Unit. (€)</label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              value={pricing.unit_price}
                              onChange={(e) => handleActivityPricingChange(
                                activityKey, 
                                'unit_price', 
                                parseFloat(e.target.value) || 0
                              )}
                            />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">Cantidad</label>
                            <input
                              type="number"
                              className="form-control"
                              value={pricing.qty}
                              onChange={(e) => handleActivityPricingChange(
                                activityKey, 
                                'qty', 
                                parseInt(e.target.value) || 1
                              )}
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Descuento (€)</label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              value={pricing.discount}
                              onChange={(e) => handleActivityPricingChange(
                                activityKey, 
                                'discount', 
                                parseFloat(e.target.value) || 0
                              )}
                            />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">IVA</label>
                            <select
                              className="form-select"
                              value={pricing.tax_profile}
                              onChange={(e) => handleActivityPricingChange(
                                activityKey, 
                                'tax_profile', 
                                e.target.value
                              )}
                            >
                              <option value="EXENTO">Exento</option>
                              <option value="IVA21">IVA 21%</option>
                            </select>
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">&nbsp;</label>
                            <button
                              className="btn btn-success w-100"
                              onClick={() => setEditingPricing(null)}
                            >
                              <SaveIcon style={{ fontSize: '16px', marginRight: '4px', verticalAlign: 'middle' }} />
                              Guardar
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Split Configuration */}
                    <div className="border-top pt-3">
                      <h6 className="text-primary mb-3">
                        <PercentIcon style={{ fontSize: '18px', marginRight: '8px', verticalAlign: 'middle' }} />
                        Configuración de Copagos
                      </h6>
                      <SplitBillingConfiguration
                        activity={{
                          key: activityKey,
                          patient_id: activity.patient_id,
                          patient_name: activity.patient_name,
                          description: activity.description,
                          total_amount: total
                        }}
                        availablePatients={availablePatients}
                        availableCompanies={availableCompanies}
                        initialSplits={activitySplits.get(activityKey)?.splits}
                        onSplitChange={(key, splits, isValid) => handleSplitChange(key, splits, isValid)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Totals Summary */}
        {selectedActivities.length > 0 && (
          <div className="card bg-light mb-4">
            <div className="card-body">
              <div className="row">
                <div className="col-md-8">
                  <h6>Resumen General</h6>
                  <p className="mb-1"><strong>Actividades:</strong> {selectedActivities.length}</p>
                  <p className="mb-0"><strong>Splits válidos:</strong> {areAllSplitsValid() ? 'Sí ✓' : 'No ✗'}</p>
                </div>
                <div className="col-md-4 text-end">
                  <div className="mb-1"><strong>Subtotal:</strong> €{totals.subtotal.toFixed(2)}</div>
                  <div className="mb-1"><strong>IVA:</strong> €{totals.totalTax.toFixed(2)}</div>
                  <div className="h4 text-success"><strong>Total:</strong> €{totals.total.toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );

  case 1: // Mostrar Sales Orders creados y botones de exportación individuales
  return (
    <Box sx={{ p: 4 }}>
      {/* Resultado de actividades creadas */}
      {createdActivities && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            ✅ Actividades Facturables Creadas
          </Typography>
          <Typography variant="body2">
            {createdActivities.created?.length || 0} actividad(es) facturable(s) creada(s)
          </Typography>
        </Alert>
      )}

      {/* Estado de creación de Sales Orders */}
      {isCreatingSalesOrders && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
            Creando Sales Orders automáticamente...
          </Typography>
        </Box>
      )}

      {/* Error al crear Sales Orders */}
      {salesOrderError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Error al crear Sales Orders
          </Typography>
          <Typography variant="body2">{salesOrderError}</Typography>
        </Alert>
      )}

      {/* Sales Orders creados exitosamente */}
      {createdSalesOrders && createdSalesOrders.sales_orders && (
        <div>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              ✅ Sales Orders Creados Automáticamente
            </Typography>
            <Typography variant="body2">
              {createdSalesOrders.sales_orders.length} Sales Order(s) generado(s)
            </Typography>
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            📋 Sales Orders Generados
          </Typography>

          {/* Lista de Sales Orders con JSONs */}
          {createdSalesOrders.sales_orders.map((so, index) => (
            <Card 
              key={index} 
              variant="outlined" 
              sx={{ 
                mb: 3,
                border: so.exported ? '2px solid #4caf50' : '1px solid rgba(0, 0, 0, 0.12)'
              }}
            >
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                      {so.order_number}
                      {so.exported && (
                        <Chip 
                          label="✅ Exportado" 
                          color="success" 
                          size="small" 
                          sx={{ ml: 2 }}
                        />
                      )}
                      {so.exporting && (
                        <Chip 
                          label="⏳ Exportando..." 
                          color="warning" 
                          size="small" 
                          sx={{ ml: 2 }}
                        />
                      )}
                      {so.export_error && (
                        <Chip 
                          label="❌ Error" 
                          color="error" 
                          size="small" 
                          sx={{ ml: 2 }}
                        />
                      )}
                    </Typography>
                    <Chip 
                      label={so.payer_type === 'Account' ? '👤 Paciente' : '🏢 Compañía'}
                      color={so.payer_type === 'Account' ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </Box>
                }
                subheader={`Pagador: ${so.payer_name}`}
              />
              <CardContent>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Actividades:</strong> {so.activities_count}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Líneas:</strong> {so.lines_count}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Error de exportación si existe */}
                {so.export_error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Error:</strong> {so.export_error}
                    </Typography>
                  </Alert>
                )}

                {/* Resultado de Odoo si fue exportado */}
                {so.odoo_result && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>✅ Exportado a Odoo</strong>
                    </Typography>
                    {so.odoo_result.exported && so.odoo_result.exported[0] && (
                      <Typography variant="caption" display="block">
                        Odoo Order ID: {so.odoo_result.exported[0].odoo_order_id}
                      </Typography>
                    )}
                  </Alert>
                )}

                {/* ✅ CORRECCIÓN: Mostrar JSON Preview generado automáticamente O resultado de exportación */}
                {(so.odoo_json_preview || so.odoo_result) && (
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      bgcolor: '#f5f5f5', 
                      maxHeight: '600px', 
                      overflow: 'auto',
                      mb: 2,
                      fontFamily: 'monospace',
                      fontSize: '11px'
                    }}
                  >
                    <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block', fontWeight: 'bold' }}>
                      📋 JSON Completo {so.exported ? 'Exportado' : 'Generado'}:
                    </Typography>
                    <pre style={{ 
                      margin: 0, 
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word'
                    }}>
                      {JSON.stringify(
                        so.odoo_result?.odoo_json || 
                        so.odoo_result?.generated_json || 
                        so.odoo_result || 
                        so.odoo_json_preview, 
                        null, 
                        2
                      )}
                    </pre>
                  </Paper>
                )}

                {/* ✅ Mensaje si aún no se ha generado el JSON */}
                {!so.odoo_json_preview && !so.odoo_result && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      ⏳ Generando JSON automáticamente...
                    </Typography>
                  </Alert>
                )}

                {/* Botones de acción por Sales Order */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {/* ✅ Solo mostrar botones si hay JSON disponible */}
                  {(so.odoo_json_preview || so.odoo_result) && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewJson(so.odoo_json_preview || so.odoo_result)}
                      >
                        Ver JSON
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={() => handleDownloadJson(so.odoo_json_preview || so.odoo_result, so.order_number)}
                      >
                        Descargar JSON
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileCopyIcon />}
                        onClick={() => handleCopyJson(so.odoo_json_preview || so.odoo_result)}
                      >
                        Copiar JSON
                      </Button>
                    </>
                  )}
                  
                  {/* ✅ BOTÓN INDIVIDUAL PARA EXPORTAR A ODOO */}
                  <Button
                    size="small"
                    variant="contained"
                    color={so.exported ? 'success' : 'primary'}
                    startIcon={
                      so.exporting ? <CircularProgress size={16} /> : 
                      so.exported ? <CheckIcon /> : 
                      <ReceiptIcon />
                    }
                    onClick={() => handleExportSingleToOdoo(so.sales_order_id, so.order_number)}
                    disabled={so.exporting || so.exported || !so.odoo_json_preview}
                  >
                    {so.exporting ? 'Exportando...' : 
                     so.exported ? 'Exportado' : 
                     '🚀 Generar en Odoo'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Si no hay Sales Orders aún y no está cargando, mostrar mensaje */}
      {!createdSalesOrders && !isCreatingSalesOrders && !salesOrderError && (
        <Alert severity="info">
          <Typography variant="body2">
            Esperando creación de Sales Orders...
          </Typography>
        </Alert>
      )}
    </Box>
  );

  case 2: // Exportar a Odoo
    return (
      <Box sx={{ p: 4 }}>
        {!exportResults ? (
          <div>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Exportar a Odoo
              </Typography>
              <Typography variant="body2">
                Genera facturas en Odoo desde los Sales Orders creados.
              </Typography>
            </Alert>
            
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={handleExportToOdoo}
              disabled={isExportingToOdoo}
              startIcon={isExportingToOdoo ? <CircularProgress size={20} /> : <ReceiptIcon />}
            >
              {isExportingToOdoo ? 'Exportando a Odoo...' : '🚀 Exportar a Odoo'}
            </Button>

            {isExportingToOdoo && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                  Exportando a Odoo... Por favor espere
                </Typography>
              </Box>
            )}
          </div>
        ) : (
          <div>
            <Alert 
              severity={exportResults.errors?.length > 0 ? "warning" : "success"}
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Resultado de la Exportación
              </Typography>
              <Typography variant="body2">
                ✅ Exitosas: {exportResults.summary?.exported || 0}
              </Typography>
              {exportResults.errors?.length > 0 && (
                <Typography variant="body2" color="error">
                  ❌ Errores: {exportResults.errors.length}
                </Typography>
              )}
            </Alert>

            {/* Facturas exportadas */}
            {exportResults.exported?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Facturas Exportadas a Odoo:
                </Typography>
                {exportResults.exported.map((exp, idx) => (
                  <Alert key={idx} severity="success" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      <strong>Sales Order:</strong> {exp.sales_order_id}<br/>
                      <strong>✅ Odoo ID:</strong> {exp.odoo_order_id}<br/>
                      <strong>Líneas:</strong> {exp.lines_count}<br/>
                      <strong>Total:</strong> €{exp.total_amount?.toFixed(2)}
                    </Typography>
                  </Alert>
                ))}
              </Box>
            )}

            {/* Errores */}
            {exportResults.errors?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" color="error" gutterBottom>
                  Errores Durante la Exportación:
                </Typography>
                {exportResults.errors.map((err, idx) => (
                  <Alert key={idx} severity="error" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      <strong>SO ID:</strong> {err.sales_order_id}<br/>
                      <strong>Error:</strong> {err.error}
                    </Typography>
                  </Alert>
                ))}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            
            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              onClick={() => setActiveStep(3)}
            >
              Ver Resumen Final
            </Button>
          </div>
        )}
      </Box>
    );

  case 3: // Resultado Final
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            🎉 Proceso Completado
          </Typography>
          <Typography variant="body2">
            Todas las operaciones se han completado exitosamente.
          </Typography>
        </Alert>

        {/* Resumen de actividades */}
        {createdActivities && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actividades Facturables Creadas
              </Typography>
              <Typography variant="body2">
                Total: {createdActivities.created?.length || 0}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Resumen de Sales Orders */}
        {createdSalesOrders && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sales Orders Creados
              </Typography>
              <Typography variant="body2">
                Total: {createdSalesOrders.sales_orders?.length || 0}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Resumen de exportación */}
        {exportResults && (
          <Card variant="outlined" sx={{ mb: 2, bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Exportación a Odoo
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Exitosas:
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {exportResults.summary?.exported || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Errores:
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {exportResults.summary?.errors || 0}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={() => {
            if (onSuccess) {
              onSuccess('Proceso completado exitosamente');
            }
            onClose();
          }}
        >
          Finalizar
        </Button>
      </Box>
    );

  default:
    return null;
}
};
// ============================================
// 🎨 RENDER PRINCIPAL
// ============================================
const handleCloseDrawer = () => {
setActiveStep(0);
setError(null);
setActivityPricing(new Map());
setActivitySplits(new Map());
setEditingPricing(null);
setCreatedActivities(null);
setCreatedSalesOrders(null);
setExportResults(null);
onClose();
};
return (
<Drawer
anchor="right"
open={open}
onClose={handleCloseDrawer}
PaperProps={{
sx: {
width: { xs: '100%', sm: '90%', md: '1400px' },
maxWidth: '100vw'
}
}}
>
{/* Header */}
<div className="d-flex justify-content-between align-items-center p-3 border-bottom">
<h5 className="mb-0">
<ReceiptIcon style={{ fontSize: '24px', marginRight: '8px', verticalAlign: 'middle' }} />
Crear Actividades Facturables y Exportar a Odoo
</h5>
<button className="btn-close" onClick={handleCloseDrawer} aria-label="Close"></button>
</div>
  {/* Progress Bar */}
  <div className="px-4 pt-4 pb-2">
    <div className="progress" style={{ height: '8px' }}>
      <div 
        className="progress-bar" 
        role="progressbar" 
        style={{ width: `${(activeStep / (wizardSteps.length - 1)) * 100}%` }}
        aria-valuenow={(activeStep / (wizardSteps.length - 1)) * 100}
        aria-valuemin="0" 
        aria-valuemax="100"
      ></div>
    </div>
    <div className="d-flex justify-content-between mt-2">
      {wizardSteps.map((step, index) => (
        <div 
          key={index}
          className={`text-center flex-fill ${index === activeStep ? 'text-primary fw-bold' : 'text-muted'}`}
          style={{ fontSize: '0.875rem' }}
        >
          <div className={`badge ${index === activeStep ? 'bg-primary' : index < activeStep ? 'bg-success' : 'bg-secondary'} mb-1`}>
            {index + 1}
          </div>
          <div>{step}</div>
        </div>
      ))}
    </div>
  </div>

  {/* Step Content */}
  <div style={{ height: 'calc(100vh - 200px)', overflowY: 'auto' }}>
    {renderStep()}
  </div>

  {/* Navigation Footer */}
  <div className="border-top p-3 bg-light">
    <div className="d-flex justify-content-between align-items-center">
      <button
        className="btn btn-outline-secondary"
        onClick={activeStep === 0 ? handleCloseDrawer : () => setActiveStep(prev => prev - 1)}
        disabled={isCreating || isCreatingSalesOrders || isExportingToOdoo}
      >
        {activeStep === 0 ? 'Cancelar' : 'Anterior'}
      </button>
      
      <Typography variant="body2" color="textSecondary">
        Paso {activeStep + 1} de {wizardSteps.length}
      </Typography>

      {/* Botón condicional según el paso */}
{activeStep === 0 && (
  <button
    className="btn btn-primary"
    onClick={handleCreateBillableActivities}
    disabled={!areAllSplitsValid() || isCreating}
  >
    {isCreating ? 'Creando...' : 'Crear Actividades'}
  </button>
)}

{activeStep === 1 && (
  <button
    className="btn btn-success"
    onClick={() => {
      if (onSuccess) {
        onSuccess('Proceso completado exitosamente');
      }
      handleCloseDrawer();
    }}
  >
    Finalizar
  </button>
)}

      
    </div>
  </div>
</Drawer>
);
};
export default AddBillingActivities;